import React, { useState, useEffect, useCallback, useMemo } from "react";
import FullCalendar from "@fullcalendar/react";
import resourceTimeGridPlugin from "@fullcalendar/resource-timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import "../../../../styles/appt-calendar.css";
import StickyHeader from "../../../../components/ModuleCommon/StickyHeader/StickyHeader";
import FormModal from "../../../../components/ACS-Dashboard/ACS-Blocks/FormModal";
import Loader from "../../../../components/ACS-Dashboard/ACS-Blocks/Loader";
import { Settings } from "lucide-react";
import timeGridPlugin from "@fullcalendar/timegrid";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { addTab } from "../../../../redux/slices/appointmentsSlices";
import Cookies from "js-cookie";
import { useRef } from "react";
import AppointmentTooltip from "../../../../components/ACS-Dashboard/ACS-Blocks/AppointmentTooltip";
import {
  addFetchedWarehouseId,
  setWarehousesForLocation,
  setLastSelectedLocation,
  setLastSelectedWarehouse,
  setLastSelectedDate,
  updateAppointmentTab,
} from "../../../../redux/slices/appointmentsSlices";
import { useSelector } from "react-redux";
import BulkSelectionModal from "../Modal/BulkSelection";
import CloseDockRangeModal from "../Modal/CloseDockRangeModal";
import ClosureWarningModal from "../Modal/ClosureWarningModal";
import EditClosureModal from "../Modal/EditClosureModal";
import {
  fetchWarehousesByLocationId,
  fetchDocksForLocationRef,
  fetchAppointmentsByLocationRef,
  fetchAppointmentById,
  cancelAppointment,
  fetchAppointmentsByCarrier,
  bulkCloseDocks,
  fetchEventsByCalendarRefId,
  deleteExceptionEvent,
  deleteEvent,
  reopenExceptionEvent,
  rescheduleAppointment
} from "../TabContent/api/AppointmentsApi";
import { fetchLocationsByOfficeCodes } from "../../Manage-Team/api";
import { selectablePointLocations } from "../../../../utils/locationHierarchy";
import notify from "../../../../components/ACS-Dashboard/ACS-Blocks/Toast";
import CancelAppointmentModal from "./Calendar-View/CancelAppointmentModal";
import {
  formatDateYMD, formatTimeHM, toTimeInput, toLocalDateTimeString,
  buildLocalDateTime, getTodayFormatted, getTimezoneAbbreviation,
  getCalendarScrollTime, toIsoLocal,
  formatTooltipTime, buildTooltipLines,
  formatAppointment,
  convertEventsToRegularHours, convertRecurringClosuresToExceptionEvents,
  CLOSED_ALL_DAY_HOURS,
  convertBlackoutExceptionToFCEvent,
  convertExceptionEventToFCEvent,
  convertRecurringEventsToFCEvents,
  parseCalendarApiResponseToDockSettings
} from "./utils";

const stopEvent = (e) => {
  e.preventDefault();
  e.stopPropagation();
};

const MyAppointments = ({ openSettingsTab, onViewAppointment, userType }) => {
  const dispatch = useDispatch();
  const calendarRef = useRef(null);
  const {
    lastSelectedLocation,
    lastSelectedWarehouse,
    lastSelectedDate,
  } = useSelector((state) => state.appointments);
  const locationApiCallCount = useRef(0);
  const lastLocationFetchKey = useRef(null);
  const carrierApiCallCount = useRef(0);
  const lastCarrierFetchKey = useRef(null);
  const dockFetchIdRef = useRef(0);
  const calendarFetchIdRef = useRef(0);
  const shipperFetchIdRef = useRef(0);
  const carrierFetchIdRef = useRef(0);

  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [selectedLocationRef, setSelectedLocationRef] = useState("");
  const [selectedDocks, setSelectedDocks] = useState([]);
  const [events, setEvents] = useState([]);
  const [exceptionEvents, setExceptionEvents] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null); 
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState(null);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [isCalendarLoading, setIsCalendarLoading] = useState(false);
  const officeCode = Cookies.get("officeCode");
  const headerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [selectedAppointmentData, setSelectedAppointmentData] = useState(null);
  const [dockBusinessHours, setDockBusinessHours] = useState({});
  const [isClosureWarningOpen, setIsClosureWarningOpen] = useState(false);
  const [closurePreviewAppointments, setClosurePreviewAppointments] = useState([]);
  const [isClosurePreviewLoading, setIsClosurePreviewLoading] = useState(false);
  const [pendingClosureBatch, setPendingClosureBatch] = useState(null);
  const [isClosingDocks, setIsClosingDocks] = useState(false);
  const [closeSelection, setCloseSelection] = useState(null);
  const [closeReason, setCloseReason] = useState("Maintenance");
  const [isSubmittingCloseRange, setIsSubmittingCloseRange] = useState(false);
  const [eventTooltip, setEventTooltip] = useState({ visible: false, event: null });
  const [suppressTooltip, setSuppressTooltip] = useState(false);
  const [exceptionToEdit, setExceptionToEdit] = useState(null);
  const [isUpdatingException, setIsUpdatingException] = useState(false);
  const [cancelModalAppointment, setCancelModalAppointment] = useState(null);
  
  const isShipper = userType === "Shipper";

  const getDockKey = useCallback((item) => {
    const raw =
      item?.dockId ??
      item?.dock_id ??
      item?.dockNumber ??
      item?.dock_number ??
      item?.resourceId ??
      item?.dock ??
      item?.id ??
      null;
    return raw !== null && raw !== undefined ? String(raw) : null;
  }, []);

  const dockNameById = useMemo(() => {
    const map = {};
    (selectedDocks || []).forEach((dock) => {
      const key = getDockKey(dock);
      if (!key) return;
      map[key] = dock.dockName || `Dock ${dock.dockNumber || dock.dockId}`;
    });
    return map;
  }, [selectedDocks, getDockKey]);
  
  const timezoneAbbr = getTimezoneAbbreviation();

  const handleInfoChange = (field, newValue) => {
    setInfoData((prev) => ({
      ...prev,
      [field]: { ...prev[field], value: newValue },
    }));

    if (field === "drop") {
      setInfoData((prev) => {
        const newInfo = { ...prev, drop: { ...prev.drop, ...newValue } };
        if (calendarRef.current && newValue.date) {
          calendarRef.current.getApi().gotoDate(newValue.date);
        }
        if (newValue.date) {
          dispatch(setLastSelectedDate(newValue.date));
        }
        return newInfo;
      });
    }

    if (field === "Location") {
      const selected = locations.find(loc => loc.locationName === newValue);
      if (selected) handleLocationSelect(selected);
    }

    if (field === "Warehouse") {
      const selected = warehouses.find(wh => wh.locationName === newValue);
      if (selected) handleWarehouseSelect(selected);
    }
  };

  const handleCalendarNav = (type) => {
    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) return;

    if (type === "today") {
      calendarApi.gotoDate(getTodayFormatted());
    } else if (type === "prev") {
      calendarApi.prev();
    } else if (type === "next") {
      calendarApi.next();
    }

    const newDate = calendarApi.getDate();
    const formattedDate = formatDateYMD(newDate);

    dispatch(setLastSelectedDate(formattedDate));
    setMonthYearText(newDate.toLocaleDateString("en-US", { month: "long", year: "numeric" }));
    setInfoData((prev) => ({ ...prev, drop: { ...prev.drop, date: formattedDate } }));
  };

  const handleDatesSet = useCallback((info) => {
    setCalendarRange({
      start: toLocalDateTimeString(info.start),
      end: toLocalDateTimeString(info.end),
    });
    const formatted = info.start.toLocaleDateString("en-US", { month: "long", year: "numeric" });
    setMonthYearText(formatted);
  }, []);

  const [modalOpen, setModalOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const fetchedWarehouseIds = useSelector(state => state.appointments.fetchedWarehouseIds);
  const warehousesByLocationId = useSelector(state => state.appointments.warehousesByLocationId);
  const [locationRefToOfficeCode, setLocationRefToOfficeCode] = useState({});
  const [calendarRange, setCalendarRange] = useState(null);
  
  const todayFormatted = getTodayFormatted();

  const suppressTooltipRef = useRef(suppressTooltip);
  useEffect(() => { suppressTooltipRef.current = suppressTooltip; }, [suppressTooltip]);

  const MODE_CONFIG = {
    add:      { label: "New",  contentType: "newAppointment"  },
    update:   { label: "Edit", contentType: "editAppointment" },
    readonly: { label: "View", contentType: "viewAppointment" },
  };

  const resolveAppointmentMode = (appointmentData) => {
    const shipmentStatus = (appointmentData?.shipmentStatus ?? appointmentData?.status ?? "").toLowerCase();
    const isInTransit = shipmentStatus === "active" || shipmentStatus === "completed";
    return isInTransit ? "readonly" : "update";
  };

  const openAppointmentTab = useCallback(async (appointmentId, modeOverride) => {
    if (!appointmentId) return;
    try {
      const appointmentData = await fetchAppointmentById(appointmentId, officeCode);
      setSelectedAppointmentData(appointmentData);

      const mode = modeOverride ?? resolveAppointmentMode(appointmentData);
      const { label, contentType } = MODE_CONFIG[mode] ?? MODE_CONFIG.update;
      const tabName = `${appointmentData?.shipmentNumber ?? "Appointment"} – ${label}`;

      dispatch(addTab({
        id:              `${mode}-appointment-${appointmentData.appointmentId}-${Date.now()}`,
        name:            tabName,
        contentType,
        appointmentData: { ...appointmentData, mode },
        isDynamic:       true,
        createdAt:       Date.now(),
        mode,
      }));
      setModalOpen(false);
    } catch (error) {
      console.error("Failed to load appointment:", error);
      notify.error({ title: "Failed to Open Appointment", message: "Could not load appointment data. Please try again." });
    }
  }, [dispatch]);

  const handleViewAppointment = async () => {
    if (!selectedEvent) return;
    await openAppointmentTab(selectedEvent.id);
  };

  useEffect(() => {
    const fetchLocationsWithWarehouses = async () => {
      try {
        const res = await fetchLocationsByOfficeCodes();
        const all = res.data || res;
        // Only top-level (POINT) locations belong on the left — WAREHOUSE rows are
        // children of a location and are populated on the right once one is selected.
        // reconstructLocationList first repairs any entries the backend's identity-reference
        // serialization collapsed to a bare id (see locationHierarchy.js) — otherwise an
        // office whose own POINT record lost that coin-flip would silently vanish here too.
        // Unnamed POINTs (shipment-stop address nodes) are dropped so they don't show as blank rows.
        const pointsOnly = Array.isArray(all) ? selectablePointLocations(all) : all;
        setLocations(pointsOnly);
      } catch (err) {
        console.error("❌ Failed to fetch locations via office codes:", err);
        setLocations([]);
      }
    };
    fetchLocationsWithWarehouses();
  }, []);

  useEffect(() => {
    if (lastSelectedDate) {
      const api = calendarRef.current?.getApi();
      const currentDateStr = api ? formatDateYMD(api.getDate()) : null;
      if (api && currentDateStr !== lastSelectedDate) {
        api.gotoDate(lastSelectedDate);
      }
    }

    if (userType !== "Shipper") return;
    if (!locations.length) return; 

    if (lastSelectedLocation && !selectedLocation) {
      const loc = locations.find(x => x.id === lastSelectedLocation.id);
      if (loc) {
        setSelectedLocation(loc);
        setSelectedLocationId(loc.id);
        setSelectedLocationRef(loc.locationRef);
        fetchDocksForLocation(loc.locationRef);
      }
    }

    if (lastSelectedWarehouse && selectedLocation && warehouses.length > 0 && !selectedWarehouse) {
      const wh = warehouses.find(x => x.id === lastSelectedWarehouse.id);
      if (wh) {
        setSelectedWarehouse(wh);
        setSelectedWarehouseId(wh.id);
        fetchDocksForLocation(wh.locationRef);
      }
    }
  }, [userType, locations, warehouses, lastSelectedLocation, lastSelectedWarehouse, lastSelectedDate]);

  useEffect(() => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      const currentDate = calendarApi.getDate();
      setMonthYearText(currentDate.toLocaleDateString("en-US", { month: "long", year: "numeric" }));
    }
  }, []);

  useEffect(() => {
    const locationId = selectedLocation?.id;
    if (!locationId) return;

    if (fetchedWarehouseIds.includes(locationId)) {
      const cached = warehousesByLocationId[locationId];
      if (cached) {
        setWarehouses(cached);
        const mapping = {};
        cached.forEach(loc => {
          const locOfficeCode = loc.officeCode || loc.parentLocation?.officeCode || officeCode;
          if (loc.locationRef && locOfficeCode) {
            mapping[loc.locationRef] = locOfficeCode;
          }
        });
        setLocationRefToOfficeCode(mapping);
      }
      return;
    }

    const fetchWarehouses = async () => {
      try {
        const response = await fetchWarehousesByLocationId(locationId);
        setWarehouses(response);
        dispatch(addFetchedWarehouseId(locationId));
        dispatch(setWarehousesForLocation({ locationId, warehouses: response }));
      } catch (error) {
        console.error("Failed to fetch warehouses:", error);
        setWarehouses([]);
      }
    };
    fetchWarehouses();
  }, [selectedLocation?.id, fetchedWarehouseIds, warehousesByLocationId, dispatch]);

  const getRangeParams = useCallback((overrideRange) => {
    const range = overrideRange || calendarRange;
    return range?.start && range?.end ? range : undefined;
  }, [calendarRange]);

  const fetchCalendarEventsForDocks = useCallback(async (docks, date) => {
    const fetchId = ++calendarFetchIdRef.current;
    const dateToUse = date || getTodayFormatted();
    const hoursMap = {};
    const allExceptionEvents = [];

    await Promise.all(
      (docks || []).map(async (dock) => {
        const dockKey = getDockKey(dock);
        if (!dockKey) return;
        if (dock.calendarRefId) {
          try {
            const events = await fetchEventsByCalendarRefId(dock.calendarRefId, dateToUse);
            if (events?.event) {
              const regularHours = convertEventsToRegularHours(events.event);
              // Calendar found but no open hours for this date: the backend won't
              // book it, so grey the whole day instead of leaving the dock white.
              if (regularHours.length > 0) {
                hoursMap[dockKey] = regularHours;
              } else if (!events.event.some((evt) => evt.event_statue_type === "OPEN")) {
                hoursMap[dockKey] = CLOSED_ALL_DAY_HOURS;
              }
              const holidayEvents = convertRecurringClosuresToExceptionEvents(events.event, dockKey);
              allExceptionEvents.push(...holidayEvents);

              const { recurringEvents } = parseCalendarApiResponseToDockSettings(events);
              allExceptionEvents.push(
                ...convertRecurringEventsToFCEvents(recurringEvents, dockKey, dateToUse)
              );

            }
            if (events?.exceptionEvent && events.exceptionEvent.length > 0) {
              events.exceptionEvent.forEach((evt) => {
                if (evt.reason === "Blackout") {
                  allExceptionEvents.push(convertBlackoutExceptionToFCEvent(evt, dockKey));
                } else {
                  allExceptionEvents.push(convertExceptionEventToFCEvent(evt, dockKey));
                }
              });
            }
          } catch (error) {
            console.error(`Failed to fetch hours for dock ${dock.dockId}:`, error);
          }
        }
      })
    );

    if (fetchId !== calendarFetchIdRef.current) return;
    setDockBusinessHours(hoursMap);
    setExceptionEvents(allExceptionEvents);
  }, [getDockKey]);

  const fetchDocksForLocation = async (locationRef) => {
    const fetchId = ++dockFetchIdRef.current;
    try {
      const data = await fetchDocksForLocationRef(locationRef);
      if (fetchId !== dockFetchIdRef.current) return;
      setSelectedDocks(data || []);
    } catch (error) {
      if (fetchId !== dockFetchIdRef.current) return;
      console.error("Failed to fetch docks for location:", error);
      setSelectedDocks([]);
    }
  };

  useEffect(() => {
    if (userType !== "Shipper") return;
    if (!selectedDocks?.length) return;
    let cancelled = false;
    (async () => {
      await fetchCalendarEventsForDocks(selectedDocks, lastSelectedDate);
      if (cancelled) return;
      requestAnimationFrame(() => {
        calendarRef.current?.getApi()?.updateSize();
      });
    })();
    return () => { cancelled = true; };
  }, [userType, selectedDocks, lastSelectedDate, fetchCalendarEventsForDocks]);

  const handleDateSelect = useCallback((info) => {
    if (userType === "Shipper") {
      const resourceId = info.resource?.id ?? info.resource?._resource?.id ?? info.resource?.extendedProps?.id ?? info.resource?.extendedProps?.resourceId ?? null;
      if (!resourceId) { notify.error("Select a dock to close on the calendar."); return; }
      const dockKey = String(resourceId);
      const dockName = dockNameById[dockKey] || info.resource?.title || `Dock ${dockKey}`;
      setCloseReason((prev) => prev || "Maintenance");
      setCloseSelection({ start: info.start, end: info.end, dockId: dockKey, dockName });
      return;
    }
    setSelectedSlot(info);
    setNewTitle("");
    setSelectedEvent(null);
    setModalOpen(true);
  }, [userType, dockNameById]);

  const handleEventClick = useCallback(async (info) => {
    if (info.event.display === "background") {
      info.jsEvent?.preventDefault();
      info.jsEvent?.stopPropagation();
      info.jsEvent?.stopImmediatePropagation();
      return;
    }
    setSuppressTooltip(true);
    setEventTooltip({ visible: false, event: null });

    const shipmentStatus = (info.event.extendedProps?.shipmentStatus ?? "").toLowerCase();
    const isInTransit = shipmentStatus === "active" || shipmentStatus === "in-transit";
    const mode = isInTransit ? "readonly" : "update";

    try {
      await openAppointmentTab(info.event.id, mode);
    } finally {
      setTimeout(() => setSuppressTooltip(false), 200);
    }
  }, [openAppointmentTab]);

  const handleDeleteEvent = async () => {
    const appointmentId = selectedEvent.id;
    try {
      await cancelAppointment(appointmentId, officeCode);
      const rangeParams = getRangeParams();

      if (userType === "Carrier") {
        const res = await fetchAppointmentsByCarrier(officeCode, rangeParams);
        const formatted = res.map((appt) => formatAppointment(appt, dockNameById, { includeCarrier: false }));
        setEvents(formatted);
      } else {
        const locationRefToUse = selectedWarehouse?.locationRef || selectedLocationRef;
        const res = await fetchAppointmentsByLocationRef(locationRefToUse, locationRefToOfficeCode[locationRefToUse], rangeParams);
        const formatted = res.map((appt) => formatAppointment(appt, dockNameById, { includeCarrier: true }));
        setEvents(formatted);
      }
      setModalOpen(false);
    } catch (error) {
      console.error("❌ Cancel failed:", error);
      alert("Could not cancel appointment.");
    }
  };

  const handleCancelAppointmentConfirm = async () => {
    if (!cancelModalAppointment?.appointmentId) return;
    try {
      await cancelAppointment(cancelModalAppointment.appointmentId, officeCode);
      const rangeParams = getRangeParams();
      if (userType === "Carrier") {
        const res = await fetchAppointmentsByCarrier(officeCode, rangeParams);
        const formatted = res.map((appt) => formatAppointment(appt, dockNameById, { includeCarrier: false }));
        setEvents(formatted);
      } else {
        const locationRefToUse = selectedWarehouse?.locationRef || selectedLocationRef;
        const res = await fetchAppointmentsByLocationRef(locationRefToUse, locationRefToOfficeCode[locationRefToUse], rangeParams);
        const formatted = res.map((appt) => formatAppointment(appt, dockNameById, { includeCarrier: true }));
        setEvents(formatted);
      }
      setCancelModalAppointment(null);
      notify.success("Appointment cancelled successfully");
    } catch (error) {
      console.error("❌ Cancel failed:", error);
      notify.error("Could not cancel appointment.");
    }
  };

  const handleEventDrop = useCallback((info) => {
    setEvents(prev => prev.map(evt =>
      evt.id === info.event.id ? { ...evt, start: info.event.startStr, end: info.event.endStr } : evt
    ));
  }, []);

  const handleWarehouseSelect = (selectedWh) => {
    if (selectedWarehouse?.id === selectedWh.id) return;
    dispatch(setLastSelectedWarehouse({
      id: selectedWh.id,
      locationRef: selectedWh.locationRef,
      locationName: selectedWh.locationName,
    }));
    setSelectedWarehouseId(selectedWh.id);
    setSelectedWarehouse(selectedWh);
    fetchDocksForLocation(selectedWh.locationRef);
  };

  const calendarWrapRef = React.useRef(null);
  const scrollRootRef = React.useRef(null);

  // Forward wheel to scroll root when FullCalendar scroller has no vertical overflow
  useEffect(() => {
    const wrap = calendarWrapRef.current;
    if (!wrap) return;
    const onWheel = (e) => {
      const tab = scrollRootRef.current || wrap.closest('.overflow-y-auto') || wrap.closest('.overflow-auto');
      if (!tab || tab.scrollHeight <= tab.clientHeight + 1) return;
      const scroller = e.target.closest?.('.fc-scroller-liquid-absolute, .fc-scroller');
      if (!scroller) return;
      if (scroller.scrollHeight > scroller.clientHeight + 1) return;
      const prev = tab.scrollTop;
      tab.scrollTop += e.deltaY;
      if (tab.scrollTop !== prev) e.preventDefault();
    };
    wrap.addEventListener('wheel', onWheel, { passive: false });
    return () => wrap.removeEventListener('wheel', onWheel);
  }, [userType]);

  React.useLayoutEffect(() => {
    const setOffset = () => {
      const offset = headerRef.current?.offsetHeight ?? 64;
      calendarWrapRef.current?.style.setProperty('--fc-sticky-offset', `${offset}px`);
    };
    setOffset();
    const ro = new ResizeObserver(setOffset);
    if (headerRef.current) ro.observe(headerRef.current);
    window.addEventListener('resize', setOffset);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', setOffset);
    };
  }, []);

  useEffect(() => {
    const wrap = calendarWrapRef.current;
    if (!wrap) return;
    const handler = (e) => {
      const deleteBtn = e.target.closest("[data-exception-delete-btn]");
      const editBtn = e.target.closest("[data-exception-edit-btn]");
      if (deleteBtn || editBtn) {
        e.stopPropagation();
        e.stopImmediatePropagation();
      }
    };
    wrap.addEventListener("click", handler, true);
    return () => wrap.removeEventListener("click", handler, true);
  }, []);

  const buildCloseBatch = (items) => {
    if (!items?.length) return [];
    const warehouseName = selectedWarehouse?.locationRef || selectedLocation?.locationRef || "";
    return items.map((it) => {
        const dockKey = getDockKey(it);
        return {
          scopeType: "DOCK",
          dockIds: dockKey ? [Number(dockKey)] : [],
          warehouseNames: warehouseName ? [warehouseName] : [],
          start: toIsoLocal(it.startDate, it.startTime),
          end: toIsoLocal(it.endDate, it.endTime),
          reason: it.reason || "Closed",
        };
      }).filter((batch) => batch.dockIds.length > 0);
  };

  const getBatchRangeParams = (batch) => {
    const times = batch.flatMap((item) => [new Date(item.start), new Date(item.end)]).filter((d) => !Number.isNaN(d.getTime()));
    if (!times.length) return null;
    const min = new Date(Math.min(...times.map((d) => d.getTime())));
    const max = new Date(Math.max(...times.map((d) => d.getTime())));
    return { start: toLocalDateTimeString(min), end: toLocalDateTimeString(max) };
  };

  const filterAppointmentsForBatch = (appointments, batch) => {
    const rangesByDock = new Map();
    batch.forEach((item) => {
      const dockId = getDockKey(item) || String(item.dockIds?.[0]);
      if (!dockId) return;
      const start = new Date(item.start);
      const end = new Date(item.end);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return;
      const list = rangesByDock.get(dockId) || [];
      list.push({ start, end });
      rangesByDock.set(dockId, list);
    });

    return (appointments || []).filter((appt) => {
      const dockId = getDockKey(appt);
      const ranges = rangesByDock.get(dockId);
      if (!ranges?.length) return false;
      const apptStart = new Date(appt.startTime);
      const apptEnd = new Date(appt.endTime);
      if (Number.isNaN(apptStart.getTime()) || Number.isNaN(apptEnd.getTime())) return false;
      return ranges.some((range) => apptStart < range.end && apptEnd > range.start);
    });
  };

  const hydrateDockNames = useCallback(
    (appointments) =>
      (appointments || []).map((appt) => ({
        ...appt,
        dockKey: getDockKey(appt),
        dockName: dockNameById[getDockKey(appt)] || (getDockKey(appt) ? `Dock ${getDockKey(appt)}` : "-"),
      })),
    [dockNameById, getDockKey]
  );

  const refreshClosurePreview = useCallback(
    async (batch) => {
      setIsClosurePreviewLoading(true);
      try {
        const ref = selectedWarehouse?.locationRef || selectedLocationRef;
        if (!ref) { setClosurePreviewAppointments([]); return; }
        const rangeParams = getBatchRangeParams(batch);
        if (!rangeParams) { setClosurePreviewAppointments([]); return; }

        const data = await fetchAppointmentsByLocationRef(ref, locationRefToOfficeCode[ref], rangeParams);
        const filtered = filterAppointmentsForBatch(data, batch);
        const hydrated = hydrateDockNames(filtered);
        setClosurePreviewAppointments(hydrated);
        return hydrated;
      } catch (err) {
        notify.error("Failed to load appointments for the selected range.");
        setClosurePreviewAppointments([]);
        return [];
      } finally {
        setIsClosurePreviewLoading(false);
      }
    },
    [selectedWarehouse?.locationRef, selectedLocationRef, locationRefToOfficeCode, hydrateDockNames]
  );

  const previewDockClosures = async (items) => {
    const batch = buildCloseBatch(items);
    setPendingClosureBatch(batch);
    const previews = await refreshClosurePreview(batch);
    if (!previews.length) {
      await handleProceedCloseDocks(batch);
      return;
    }
    setIsClosureWarningOpen(true);
  };

  const handleProceedCloseDocks = async (batchOverride) => {
    const batchToUse = batchOverride || pendingClosureBatch;
    if (!batchToUse?.length) {
      setIsClosureWarningOpen(false);
      return;
    }
    setIsClosingDocks(true);
    try {
      const ref = selectedWarehouse?.locationRef || selectedLocationRef;
      await bulkCloseDocks(batchToUse, locationRefToOfficeCode[ref]);
      setIsClosureWarningOpen(false);
      setIsBulkModalOpen(false);
      setPendingClosureBatch(null);
      if (ref) await fetchAppointments(
        ref, locationRefToOfficeCode[ref], getRangeParams(), "after bulk close", { force: true }
      );
      await fetchDocksForLocation(ref);
    } catch (err) {
      notify.error("Failed to close some docks.");
    } finally {
      setIsClosingDocks(false);
    }
  };

  const handleRescheduleFromWarning = async (appointment, selection) => {
    if (!appointment?.appointmentId || !selection?.slot || !selection?.date) return;
    const payload = {
      startTime: `${selection.date}T${selection.slot.startTime}`,
      endTime: `${selection.date}T${selection.slot.endTime}`,
      dockNumber: getDockKey(selection),
      doorNumber: appointment?.doorNumber || null,
      warehouse: selection.warehouseRef || appointment?.warehouse || selectedWarehouse?.locationRef || selectedLocationRef,
    };

    try {
      const rescheduled = await rescheduleAppointment(appointment.appointmentId, payload, officeCode);
      if (pendingClosureBatch?.length) await refreshClosurePreview(pendingClosureBatch);
      const updated = rescheduled?.appointmentId ? rescheduled : await fetchAppointmentById(appointment.appointmentId, officeCode);
      if (updated?.appointmentId) {
        dispatch(updateAppointmentTab({ appointmentId: updated.appointmentId, appointmentData: updated }));
      }
      const ref = selectedWarehouse?.locationRef || selectedLocationRef;
      if (ref) {
        await fetchAppointments(ref, locationRefToOfficeCode[ref], getRangeParams(), "after reschedule from warning", { force: true });
      }
    } catch (err) {
      if (err?.response?.status === 409) {
        notify.error("Selected slot is no longer available.");
      } else {
        notify.error("Failed to reschedule appointment.");
      }
      throw err;
    }
  };

  const handleBulkCloseConfirm = async (items) => {
    await previewDockClosures(items);
  };

  const handleCloseDockSelectionConfirm = async () => {
    if (!closeSelection?.dockId || !closeSelection?.start || !closeSelection?.end) {
      setCloseSelection(null);
      return;
    }
    const payload = {
      dockId: closeSelection.dockId,
      dockName: closeSelection.dockName,
      startDate: formatDateYMD(closeSelection.start),
      endDate: formatDateYMD(closeSelection.end),
      startTime: formatTimeHM(closeSelection.start),
      endTime: formatTimeHM(closeSelection.end),
      reason: closeReason || "Closed",
    };
    if (payload.startDate === payload.endDate && payload.endTime <= payload.startTime) {
      notify.error("End time must be after start time.");
      return;
    }
    setIsSubmittingCloseRange(true);
    try {
      await previewDockClosures([payload]);
      setCloseSelection(null);
    } catch (err) {
      notify.error("Failed to close this range. Please try again.");
    } finally {
      setIsSubmittingCloseRange(false);
    }
  };

  const handleCloseDockSelectionCancel = () => {
    setCloseSelection(null);
  };

  const handleDeleteException = useCallback(async (exceptionId, exceptionEvent = null, recurringEventId = null) => {
    if (!exceptionId && !recurringEventId) return;
    const ref = selectedWarehouse?.locationRef || selectedLocationRef;
    if (!ref) { notify.error("Select a location to refresh closures."); return; }
    setIsCalendarLoading(true);
    try {
      // A recurring-event-derived block has no real exception_event row — deleting
      // it means removing the underlying recurring Event via the events endpoint,
      // not the exception-events endpoint (which has nothing matching to delete).
      if (recurringEventId) {
        await deleteEvent(recurringEventId);
      } else {
        await deleteExceptionEvent(exceptionId);
      }
      const dockLabel = exceptionEvent?.resourceId ? dockNameById[String(exceptionEvent.resourceId)] || `Dock ${exceptionEvent.resourceId}` : "Dock";
      const startStr = exceptionEvent?.start ? new Date(exceptionEvent.start).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : null;
      const endStr = exceptionEvent?.end ? new Date(exceptionEvent.end).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : null;
      const timePart = startStr && endStr ? ` (${startStr} - ${endStr})` : "";
      notify.success(`Removed closure${timePart} for ${dockLabel}.`);
      await fetchDocksForLocation(ref);
      await fetchAppointments(ref, locationRefToOfficeCode[ref], getRangeParams(), "after exception delete", { force: true });
    } catch (err) {
      console.error("❌ Failed to delete exception event:", err);
      notify.error("Failed to remove closure.");
    } finally {
      setIsCalendarLoading(false);
    }
  });

  const startEditException = useCallback((exceptionEvent) => {
    if (!exceptionEvent) return;
    const start = new Date(exceptionEvent.start);
    const rawEnd = new Date(exceptionEvent.end);
    const end = rawEnd.getHours() === 0 && rawEnd.getMinutes() === 0 ? new Date(rawEnd.getTime() - 60000) : rawEnd;

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) { notify.error("Unable to edit this closure."); return; }
    const sameDay = start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth() && start.getDate() === end.getDate();
    if (!sameDay) {
      notify.error("Editing multi-day closures is not supported. Remove and re-create the desired ranges.");
      return;
    }
    const dateStr = formatDateYMD(start);
    const defaultFrom = toTimeInput(start);
    const defaultTo = toTimeInput(end);

    setExceptionToEdit({
      exceptionId: exceptionEvent.extendedProps?.exceptionId,
      dockId: exceptionEvent.resourceId,
      dockName: dockNameById[String(exceptionEvent.resourceId)] || exceptionEvent.title || "Dock",
      dateStr, start, end, reopenFrom: defaultFrom, reopenTo: defaultTo,
    });
  });

  const handleReopenException = async () => {
    if (!exceptionToEdit?.exceptionId) { setExceptionToEdit(null); return; }
    const ref = selectedWarehouse?.locationRef || selectedLocationRef;
    if (!ref) { notify.error("Select a location to refresh closures."); return; }

    const reopenStartDate = buildLocalDateTime(exceptionToEdit.dateStr, exceptionToEdit.reopenFrom);
    const reopenEndDate = buildLocalDateTime(exceptionToEdit.dateStr, exceptionToEdit.reopenTo);

    if (!reopenStartDate || !reopenEndDate) { notify.error("Provide both start and end times."); return; }
    if (!(reopenEndDate > reopenStartDate)) { notify.error("Reopen end must be after start."); return; }
    if (reopenStartDate < exceptionToEdit.start || reopenEndDate > exceptionToEdit.end) { notify.error("Reopen window must be inside the existing closure."); return; }

    setIsUpdatingException(true);
    try {
      await reopenExceptionEvent(exceptionToEdit.exceptionId, { openStart: toLocalDateTimeString(reopenStartDate), openEnd: toLocalDateTimeString(reopenEndDate) });
      notify.success("Closure updated.");
      setExceptionToEdit(null);
      await fetchDocksForLocation(ref);
      await fetchAppointments(ref, locationRefToOfficeCode[ref], getRangeParams(), "after exception edit", { force: true });
    } catch (err) {
      console.error("❌ Failed to edit exception event:", err);
      const msg = err?.response?.data || err?.message || "Failed to update closure.";
      notify.error(typeof msg === "string" ? msg : "Failed to update closure.");
    } finally {
      setIsUpdatingException(false);
    }
  };

  const handleEventResize = useCallback((info) => {
    setEvents(prev => prev.map(evt => evt.id === info.event.id ? { ...evt, start: info.event.startStr, end: info.event.endStr } : evt));
  }, []);

  const handleDeleteExceptionRef = useRef(handleDeleteException);
  const startEditExceptionRef = useRef(startEditException);
  useEffect(() => { handleDeleteExceptionRef.current = handleDeleteException; }, [handleDeleteException]);
  useEffect(() => { startEditExceptionRef.current = startEditException; }, [startEditException]);


  // DOM injection for background/exception events
  const handleEventDidMount = useCallback((info) => {
    const isBackground = info.event.display === "background" || info.event.extendedProps?.rendering === "background";
    const exceptionId = info.event.extendedProps?.exceptionId;
    const recurringEventId = info.event.extendedProps?.recurringEventId;

    // Trigger injection if it's explicitly a background event OR has an exception ID
    if (isBackground || exceptionId || recurringEventId) {

      // Maintain the proper color and opacity
      info.el.style.setProperty("background-color", "#fee2e2", "important");
      
      // Force full width and remove outlines/margins
      info.el.style.setProperty("left", "0px", "important");
      info.el.style.setProperty("right", "0px", "important");
      info.el.style.setProperty("width", "100%", "important");
      info.el.style.setProperty("margin", "0px", "important");
      info.el.style.setProperty("border", "none", "important");
      info.el.style.setProperty("outline", "none", "important");

      // Make the entire background block non-clickable
      info.el.style.setProperty("pointer-events", "none", "important");
      
      // Prevent duplicate injection if FullCalendar recycles the node
      if (info.el.querySelector(".injected-bg-content")) return;

      // Create the inner wrapper
      const wrapper = document.createElement("div");
      wrapper.className = "injected-bg-content";
      Object.assign(wrapper.style, {
        position: "absolute", 
        inset: "0px", 
        padding: "4px 8px",
        color: "#223688",
        zIndex: "50", 
        display: "flex",
        flexDirection: "column",
        pointerEvents: "none",
        overflow: "hidden"
      });

      // 4. Create a flex column container for stacked text
      const textContainer = document.createElement("div");
      Object.assign(textContainer.style, {
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        gap: "2px",
        minWidth: "0",
        overflow: "hidden",
        paddingRight: "55px"
      });

      // Extract and Format the Time
      let timeString = "";
      if (info.event.extendedProps?.isWholeDay) {
        timeString = "Closed (all day)";
      } else if (info.event.start) {
        const startTime = new Date(info.event.start).toLocaleTimeString([], { hour: "numeric", minute: "2-digit", hour12: true });
        timeString = startTime;
        if (info.event.end) {
          const endTime = new Date(info.event.end).toLocaleTimeString([], { hour: "numeric", minute: "2-digit", hour12: true });
          timeString += ` - ${endTime}`;
        }
      }

      // 5. Append Time Text (Top Row)
      if (timeString) {
        const timeSpan = document.createElement("span");
        timeSpan.innerText = timeString;
        Object.assign(timeSpan.style, {
          fontSize: "0.8rem", 
          flexShrink: "0" 
        });
        textContainer.appendChild(timeSpan);
      }

      // 6. Append Reason Text (Bottom Row)
      const reasonText = info.event.title || info.event.extendedProps?.reason || "";
      if (reasonText) {
        const reasonSpan = document.createElement("span");
        reasonSpan.innerText = reasonText;
        reasonSpan.title = reasonText; 
        Object.assign(reasonSpan.style, {
          fontSize: "0.95rem", 
          fontWeight: "700", 
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap" 
        });
        textContainer.appendChild(reasonSpan);
      }

      // Add the stacked text container to the wrapper
      wrapper.appendChild(textContainer);

      // 7. Append Buttons (Shipper Only)
      if (userType === "Shipper" && (exceptionId || recurringEventId)) {
        const btnContainer = document.createElement("div");
        Object.assign(btnContainer.style, {
          position: "absolute",
          top: "4px",
          right: "4px",
          display: "flex",
          gap: "4px",
          zIndex: "100",
          pointerEvents: "auto" // CRITICAL: Bring clicks back to life ONLY for this button container
        });

        // Edit Button — only for real one-time exceptions (blackouts/holidays).
        // A recurring-event block has no single-date "reopen" concept; edit its
        // pattern from Dock Settings > Recurring Events instead.
        if (exceptionId) {
          const editBtn = document.createElement("button");
          editBtn.innerHTML = "✎";
          editBtn.setAttribute("aria-label", "Edit closure");
          Object.assign(editBtn.style, {
            width: "22px", height: "22px", borderRadius: "9999px",
            border: "1px solid rgba(0,0,0,0.1)", background: "rgba(255,255,255,0.95)",
            color: "#1f2937", fontSize: "12px", display: "flex", alignItems: "center",
            justifyContent: "center", cursor: "pointer", padding: "0"
          });
          editBtn.onmousedown = (e) => e.stopPropagation();
          editBtn.onclick = (e) => {
            e.preventDefault(); e.stopPropagation();
            startEditExceptionRef.current(info.event);
          };
          btnContainer.appendChild(editBtn);
        }

        // Delete Button
        const deleteBtn = document.createElement("button");
        deleteBtn.innerHTML = "×";
        deleteBtn.setAttribute("aria-label", recurringEventId ? "Delete recurring event" : "Delete closure");
        Object.assign(deleteBtn.style, {
          width: "22px", height: "22px", borderRadius: "9999px",
          border: "1px solid rgba(0,0,0,0.1)", background: "rgba(255,255,255,0.95)",
          color: "#b91c1c", fontSize: "14px", display: "flex", alignItems: "center",
          justifyContent: "center", cursor: "pointer", padding: "0", fontWeight: "bold"
        });
        deleteBtn.onmousedown = (e) => e.stopPropagation();
        deleteBtn.onclick = (e) => {
          e.preventDefault(); e.stopPropagation();
          handleDeleteExceptionRef.current(exceptionId, info.event, recurringEventId);
        };
        btnContainer.appendChild(deleteBtn);
        wrapper.appendChild(btnContainer);
      }

      info.el.appendChild(wrapper);
    }
  }, [userType]);


const handleEventMouseEnter = useCallback((info) => {
    if (suppressTooltipRef.current) return;
    if (!info?.event || !info?.el) return;
    
    // Block the tooltip from showing on background events OR exception events
    if (
      info.event.display === "background" || 
      info.event.extendedProps?.exceptionId
    ) {
      return;
    }

    const rect = info.el.getBoundingClientRect();
    setEventTooltip({
      visible: true,
      event: info.event,
      x: rect.left + rect.width / 2,
      y: rect.top,
    });

    const scroller = info.el.closest(".fc-scroller-liquid-absolute") ?? info.el.closest(".fc-scroller") ?? calendarWrapRef.current;
    const dismiss = () => setEventTooltip({ visible: false, event: null });
    scroller?.addEventListener("scroll", dismiss, { passive: true, once: true });
    info.el._tooltipDismiss = () => scroller?.removeEventListener("scroll", dismiss);
  }, []);

  const handleEventMouseLeave = useCallback((info) => {
    info?.el?._tooltipDismiss?.();
    info.el._tooltipDismiss = null;
    setEventTooltip({ visible: false, event: null });
  }, []);

  const buttons = useMemo(() => {
    if (userType !== "Shipper") return [];
    return [
      { label: "Settings", type: "primary", icon: <Settings size={11} />, onClick: openSettingsTab },
      { label: "Close Docks", type: "primary", dropdownOffsetY: "right-[-12px]", color: "red", onClick: () => { setIsOpen(false); setIsBulkModalOpen(true); } },
    ];
  }, [userType, openSettingsTab]);

  const fetchAppointments = async (warehouseRef, officeCodeArg, rangeOverride, reason = "unspecified", options = {}) => {
    const { force = false } = options;
    if (userType !== "Shipper") return;
    const fetchId = ++shipperFetchIdRef.current;
    try {
      if (!warehouseRef) { setEvents([]); return; }
      const officeCodeToUse = locationRefToOfficeCode[warehouseRef] || officeCodeArg || officeCode;
      const rangeParams = getRangeParams(rangeOverride);
      if (!rangeParams) return;
      
      const fetchKey = `${warehouseRef}|${officeCodeToUse}|${rangeParams.start}|${rangeParams.end}`;
      if (!force && lastLocationFetchKey.current === fetchKey && events.length > 0) return;
      
      lastLocationFetchKey.current = fetchKey;
      locationApiCallCount.current += 1;
      setIsCalendarLoading(true);
      const data = await fetchAppointmentsByLocationRef(warehouseRef, officeCodeToUse, rangeParams);
      if (fetchId !== shipperFetchIdRef.current) return;
      
      if (!data || data.length === 0) { setEvents([]); return; }
      const formatted = data.map((appt) => formatAppointment(appt, dockNameById, { includeCarrier: true }));
      setEvents(formatted);
    } catch (error) {
      if (fetchId !== shipperFetchIdRef.current) return;
      console.error("Failed to load appointments:", error);
      setEvents([]);
    } finally {
      if (fetchId === shipperFetchIdRef.current) setIsCalendarLoading(false);
    }
  };

  useEffect(() => {
    const updateCalendarHeaderPosition = () => {
      if (headerRef.current) {
        const headerHeight = headerRef.current.offsetHeight;
        const colHeaders = document.querySelectorAll('.fc-col-header, .fc-scrollgrid-section-header, .fc-scrollgrid-section.fc-scrollgrid-section-header');
        colHeaders.forEach(header => { header.style.top = `${headerHeight}px`; });
      }
    };
    updateCalendarHeaderPosition();
    const timer = setTimeout(updateCalendarHeaderPosition, 100);
    window.addEventListener('resize', updateCalendarHeaderPosition);
    return () => { clearTimeout(timer); window.removeEventListener('resize', updateCalendarHeaderPosition); };
  }, [userType, selectedLocation, selectedWarehouse]);

  const docksResources = useMemo(() => 
    selectedDocks.map((dock) => {
        const key = getDockKey(dock);
        if (!key) return null;
        return {
          id: key,
          title: dock.dockName || `Dock ${dock.dockNumber || dock.dockId}`,
          // undefined, not [], when hours are unknown: FullCalendar treats [] as
          // "has hours" and then greys nothing on that dock
          businessHours: dockBusinessHours[key],
        };
      }).filter(Boolean),
  [selectedDocks, dockBusinessHours, getDockKey]);

  useEffect(() => {
    if (calendarRef.current) calendarRef.current.getApi().refetchResources();
  }, [selectedDocks]);

  const handleLocationSelect = (selectedLoc) => {
    if (selectedLocation?.id === selectedLoc.id) return;
    dispatch(setLastSelectedLocation({ id: selectedLoc.id, locationRef: selectedLoc.locationRef, locationName: selectedLoc.locationName }));
    setSelectedLocationId(selectedLoc.id);
    setSelectedLocationRef(selectedLoc.locationRef);
    setSelectedLocation(selectedLoc);
    setSelectedWarehouse(null);
    setSelectedWarehouseId(null);
    setWarehouses([]);
    setSelectedDocks([]);
    fetchDocksForLocation(selectedLoc.locationRef);
  };

  useEffect(() => {
    const api = calendarRef.current?.getApi();
    if (!api || !calendarWrapRef.current) return;

    let frameId = null;
    let isUnmounted = false;
    const scheduleUpdate = (options = {}) => {
      if (frameId !== null) return;
      frameId = requestAnimationFrame(() => {
        frameId = null;
        if (isUnmounted) return;
        api.updateSize();
        if (options.refreshResources && typeof api.refetchResources === "function") api.refetchResources();
        if (options.rerenderEvents && typeof api.rerenderEvents === "function") api.rerenderEvents();
      });
    };

    const resizeObserver = new ResizeObserver(() => scheduleUpdate());
    resizeObserver.observe(calendarWrapRef.current);

    const handleVisibility = () => {
      if (document.visibilityState && document.visibilityState !== "visible") return;
      scheduleUpdate({ refreshResources: true, rerenderEvents: true });
    };

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("focus", handleVisibility);
    window.addEventListener("pageshow", handleVisibility);
    scheduleUpdate();

    return () => {
      isUnmounted = true;
      if (frameId !== null) cancelAnimationFrame(frameId);
      resizeObserver.disconnect();
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("focus", handleVisibility);
      window.removeEventListener("pageshow", handleVisibility);
    };
  }, []);

  useEffect(() => {
    if (userType === "Carrier") {
      const reason = calendarRange ? "calendar range change" : "initial load";
      loadAppointmentsByCarrier(officeCode, getRangeParams(), reason);
    }
  }, [userType, officeCode, getRangeParams, calendarRange]);

  const loadAppointmentsByCarrier = async (officeCodeCarrier, rangeOverride, reason = "unspecified", options = {}) => {
    const fetchId = ++carrierFetchIdRef.current;
    try {
      const { force = false } = options;
      const rangeParams = getRangeParams(rangeOverride);
      if (!rangeParams) return;
      const fetchKey = `${officeCodeCarrier}|${rangeParams.start}|${rangeParams.end}`;
      if (!force && lastCarrierFetchKey.current === fetchKey && events.length > 0) return;
      
      lastCarrierFetchKey.current = fetchKey;
      carrierApiCallCount.current += 1;
      setIsCalendarLoading(true);
      const res = await fetchAppointmentsByCarrier(officeCodeCarrier, rangeParams);
      if (fetchId !== carrierFetchIdRef.current) return;
      
      if (!res || res.length === 0) { setEvents([]); return; }
      const formatted = res.map((appt) => formatAppointment(appt, dockNameById, { includeCarrier: false }));
      setEvents(formatted);
    } catch (error) {
      if (fetchId !== carrierFetchIdRef.current) return;
      console.error("❌ Failed to load carrier appointments:", error);
      setEvents([]);
    } finally {
      if (fetchId === carrierFetchIdRef.current) setIsCalendarLoading(false);
    }
  };

  useEffect(() => {
    if (userType !== "Shipper") return;
    const ref = selectedWarehouse?.locationRef || selectedLocationRef;
    if (!ref) return;
    if (lastSelectedWarehouse && !selectedWarehouse && warehouses.length === 0) return;

    const reason = locationApiCallCount.current === 0 ? "initial load" : "calendar range change";
    fetchAppointments(ref, locationRefToOfficeCode[ref], getRangeParams(), reason);
  }, [userType, selectedWarehouse?.locationRef, selectedLocationRef, locationRefToOfficeCode, getRangeParams, calendarRange, lastSelectedWarehouse, warehouses.length]);

  const [monthYearText, setMonthYearText] = useState("");

  const [infoData, setInfoData] = useState({
    drop: { date: lastSelectedDate || todayFormatted, timezone: timezoneAbbr || "PST" },
    Location: { label: "Location", type: "dropdown", value: "Select Location", options: locations.map(loc => loc.locationName), isPlaceholder: true },
    Warehouse: { type: "dropdown", label: "Warehouse", value: "Select Warehouse", options: warehouses.map(wh => wh.locationName), isPlaceholder: true },
  });
    
  useEffect(() => {
    setInfoData((prev) => {
      const updated = { drop: { date: prev.drop?.date || lastSelectedDate, timezone: prev.drop?.timezone || timezoneAbbr } };
      if (userType === "Shipper") {
        updated.Location = { label: "Location", type: "dropdown", value: selectedLocation?.locationName || "Select Location", options: locations.map(l => l.locationName), isPlaceholder: !selectedLocation };
        updated.Warehouse = { label: "Warehouse", type: "dropdown", value: selectedWarehouse?.locationName || "Select Warehouse", options: warehouses.map(w => w.locationName), isPlaceholder: !selectedWarehouse };
      }
      return updated;
    });
  }, [userType, selectedLocation, selectedWarehouse, locations, warehouses]);
 
  // ==========================================
  // STANDARD APPOINTMENT EVENTS (React JSX)
  // ==========================================
  const renderEventContent = useCallback((eventInfo) => {
    const isBackground = eventInfo.event.display === "background" || eventInfo.event.extendedProps?.rendering === "background";
    const isException = eventInfo.event.extendedProps?.exceptionId;

    if (isBackground || isException) return null;

    const isInTransit = ["Active", "In Transit"].includes(eventInfo.event.extendedProps?.shipmentStatus);
    const { shipmentNumber, destination, carrierName, orderNumber } = eventInfo.event.extendedProps || {};
    const timeStr = new Date(eventInfo.event.start).toLocaleTimeString([], { hour: "numeric", minute: "2-digit", hour12: true });

    const cellStyle = { fontSize: "0.87rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
    const labelStyle = { fontSize: "0.6rem", opacity: 0.65, marginRight: "2px", textTransform: "uppercase", letterSpacing: "0.03em" };

    return (
      <div
        className="event-content-wrapper"
        style={{
          position: "relative", height: "100%", boxSizing: "border-box", overflow: "hidden",
          padding: "4px 8px", opacity: isInTransit ? 0.7 : 1,
          display: "flex", flexDirection: "column", gap: "3px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "6px", minWidth: 0, overflow: "hidden" }}>
          <span style={{ fontSize: "0.8rem", flexShrink: 0 }}>{timeStr}</span>
          <span style={{ fontSize: "0.95rem", fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={shipmentNumber}>
            {shipmentNumber || "—"}
          </span>
          {isInTransit && <span style={{ fontSize: "0.6rem", fontStyle: "italic", opacity: 0.9, whiteSpace: "nowrap", flexShrink: 0 }}>In-Transit</span>}
        </div>

        <div style={{ borderBottom: "1px solid rgba(255,255,255,0.3)", margin: "0" }} />

        {isShipper && (
          <div style={{ minWidth: 0 }}>
            <span style={cellStyle} title={carrierName}>
              <span style={labelStyle}>Carrier:</span> {carrierName || "—"}
            </span>
          </div>
        )}

        <div
          style={{
            display: isShipper ? "grid" : "flex",
            gridTemplateColumns: isShipper ? "1fr 1fr" : undefined,
            flexDirection: isShipper ? undefined : "column",
            gap: "4px",
            minWidth: 0
          }}
        >
          <span style={cellStyle}>
            <span style={labelStyle}>Dest:</span> {destination || "—"}
          </span>
          <span style={cellStyle}>
            <span style={labelStyle}>Order #:</span> {orderNumber || "—"}
          </span>
        </div>

        {isShipper && eventInfo.event.id && (
          <button
            type="button"
            aria-label="Cancel appointment"
            onClick={(e) => {
              e.preventDefault(); e.stopPropagation();
              setCancelModalAppointment({
                appointmentId:  eventInfo.event.id, carrierName: carrierName || "-", shipmentNumber: shipmentNumber || "-",
                shipmentStatus: eventInfo.event.extendedProps?.shipmentStatus || "-", destination: eventInfo.event.extendedProps?.shipmentStops?.at(-1)?.stopName || "-",
                orderNumber: orderNumber || "-", stopNumber: eventInfo.event.extendedProps?.stopNumber || "-",
                dockName: eventInfo.event.extendedProps?.dockName || "-", requestType: eventInfo.event.extendedProps?.requestType || "-",
                startTime: eventInfo.event.start, endTime: eventInfo.event.end,
              });
            }}
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              position: "absolute", top: "2px", right: "2px", width: "16px", height: "16px", borderRadius: "9999px",
              border: "1px solid rgba(0,0,0,0.1)", background: "rgba(255,255,255,0.95)", color: "#b91c1c", fontSize: "12px", lineHeight: "14px",
              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: "0", zIndex: "10",
            }}
          >
            ×
          </button>
        )}
      </div>
    );
  }, [userType]);

  const allEvents = useMemo(() => [...events, ...exceptionEvents], [events, exceptionEvents]);
  const calendarScrollTime = useRef(getCalendarScrollTime()).current;

  return (
    <div ref={scrollRootRef} className="flex flex-col flex-1 min-h-0 h-full overflow-y-auto scrollbar-hide">
      <div ref={headerRef} data-app-sticky-header className="sticky top-0 z-10 bg-white shadow-md w-full">
        <StickyHeader mode="calendarSettings" infoData={infoData} onInfoChange={handleInfoChange} selectedLocation={selectedLocation} selectedWarehouse={selectedWarehouse} buttons={buttons} onCalendarNav={handleCalendarNav} />
      </div>
      <AppointmentTooltip tooltip={eventTooltip} />

      {userType === "Carrier" ? (
        <div ref={calendarWrapRef} className="relative" onMouseLeave={() => setEventTooltip({ visible: false, event: null })}>
          {isCalendarLoading && <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/70 backdrop-blur-[1px] pointer-events-none"><Loader message="Loading appointments..." /></div>}
          <FullCalendar
            ref={calendarRef}
            plugins={[timeGridPlugin, interactionPlugin]}
            initialView="timeGridWeek"
            firstDay={1}
            nowIndicator={true}
            headerToolbar={false}
            scrollTime={calendarScrollTime} 
            events={allEvents}
            stickyHeaderDates={false}
            selectable
            editable={false}
            eventResizableFromStart
            eventMouseEnter={handleEventMouseEnter}
            eventMouseLeave={handleEventMouseLeave}
            slotMinTime="00:00:00"
            slotMaxTime="24:00:00"
            slotDuration="00:15:00"
            slotLabelInterval="01:00"
            snapDuration="00:15:00"
            eventMinHeight={15}
            allDaySlot={false}
            height="auto"
            datesSet={handleDatesSet}
            select={handleDateSelect}
            eventDrop={handleEventDrop}
            eventResize={handleEventResize}
            eventClick={handleEventClick}
            eventDidMount={handleEventDidMount} // <--- UNCOMMENTED
            eventContent={renderEventContent}
            className="fc-timeGridWeek-view"
          />
        </div>
      ) : userType === "Shipper" ? (
        <div ref={calendarWrapRef} className="relative" onMouseLeave={() => setEventTooltip({ visible: false, event: null })}>
          {isCalendarLoading && <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/70 backdrop-blur-[1px] pointer-events-none"><Loader message="Loading appointments..." /></div>}
          <FullCalendar
            schedulerLicenseKey="CC-Attribution-NonCommercial-NoDerivatives"
            ref={calendarRef}
            plugins={[resourceTimeGridPlugin, interactionPlugin]}
            headerToolbar={false}
            initialView="resourceTimeGridDay"
            nowIndicator={true}
            scrollTime={calendarScrollTime} 
            resources={docksResources}
            events={allEvents}
            eventContent={renderEventContent}
            selectable
            editable={false}
            selectConstraint={"businessHours"}
            eventResizableFromStart
            slotMinTime="00:00:00"
            slotMaxTime="24:00:00"
            slotDuration="00:15:00"
            slotLabelInterval="01:00"
            snapDuration="00:15:00"
            eventMinHeight={15}
            displayEventEnd={true}
            allDaySlot={false}
            height="auto"
            datesSet={handleDatesSet}
            select={handleDateSelect}
            eventDrop={handleEventDrop}
            eventResize={handleEventResize}
            eventClick={handleEventClick}
            eventOverlap={false}
            stickyHeaderDates={false}
            stickyFooterScrollbar={true}
            eventMouseEnter={handleEventMouseEnter}
            eventMouseLeave={handleEventMouseLeave}
            eventDidMount={handleEventDidMount} // <--- UNCOMMENTED
            className="fc-resourceTimeGridDay-view"
          />
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center h-[200px] gap-2 text-gray-500">
          <svg className="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"></path>
          </svg>
          <span className="text-sm">Preparing your view...</span>
        </div>
      )}

      {modalOpen && <FormModal title={"Appointment Details"} value={newTitle} setValue={setNewTitle} onClose={() => setModalOpen(false)} onView={handleViewAppointment} onDelete={selectedEvent ? handleDeleteEvent : null} />}
      {isBulkModalOpen && <BulkSelectionModal onClose={() => setIsBulkModalOpen(false)} selectedWarehouseName={selectedWarehouse?.locationName} rows={selectedDocks} onConfirm={handleBulkCloseConfirm} selectedDate={lastSelectedDate} />}
      {closeSelection && <CloseDockRangeModal selection={closeSelection} dockName={closeSelection.dockName} reason={closeReason} onReasonChange={setCloseReason} onClose={handleCloseDockSelectionCancel} onConfirm={handleCloseDockSelectionConfirm} isSubmitting={isSubmittingCloseRange} timezoneAbbr={timezoneAbbr} />}
      {isClosureWarningOpen && <ClosureWarningModal appointments={closurePreviewAppointments} warehouses={warehouses} defaultWarehouseRef={selectedWarehouse?.locationRef || selectedLocationRef || ""} isLoading={isClosurePreviewLoading} isSubmitting={isClosingDocks} onCancel={() => { setIsClosureWarningOpen(false); setPendingClosureBatch(null); }} onProceed={() => handleProceedCloseDocks(pendingClosureBatch)} onReschedule={handleRescheduleFromWarning} title="Appointments in the selected close range" />}
      {exceptionToEdit && <EditClosureModal data={exceptionToEdit} isSaving={isUpdatingException} onClose={() => setExceptionToEdit(null)} onSave={handleReopenException} onFieldChange={(field, value) => setExceptionToEdit((prev) => ({ ...prev, [field]: value }))} formatTime={formatTooltipTime} />}
      {cancelModalAppointment && <CancelAppointmentModal appointment={cancelModalAppointment} dockNameById={dockNameById} onClose={() => setCancelModalAppointment(null)} onConfirm={handleCancelAppointmentConfirm} />}
    </div>
  );
};

export default MyAppointments;